import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2024', title: '品牌诞生', description: '非麻瓜漫画正式创立，以「魔法世界，非麻瓜专属」为理念，致力于打造最优质的魔法奇幻漫画阅读平台。' },
  { year: '2025', title: '快速发展', description: '平台漫画数量突破500部，涵盖魔法、奇幻、异能、玄幻等多个热门品类。月活跃读者突破100万。' },
  { year: '2025', title: '创作者入驻', description: '推出创作者扶持计划，吸引了超过200位优秀的漫画作者和画师入驻平台，原创内容占比大幅提升。' },
  { year: '2026', title: '全新体验', description: '推出下拉式阅读模式和智能推荐系统，全面提升阅读体验。平台月活跃用户突破500万，成为国内领先的魔法奇幻漫画平台。' },
];

const values = [
  { icon: 'ri-magic-line', title: '魔法世界', desc: '我们相信每个人心中都有一个魔法世界。非麻瓜漫画致力于为读者打开通往奇幻世界的大门。' },
  { icon: 'ri-book-open-line', title: '优质内容', desc: '坚持严格的漫画筛选标准，只呈现最优质、最精彩的内容，让每一位读者都能找到心仪的作品。' },
  { icon: 'ri-user-heart-line', title: '读者至上', desc: '以读者体验为核心，不断优化阅读功能。下拉式阅读、夜间模式、智能书签，让阅读更加便捷舒适。' },
  { icon: 'ri-team-line', title: '创作者伙伴', desc: '与创作者建立长期稳定的合作关系，提供专业的创作支持、推广资源和合理的收益模式。' },
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="关于我们 - 非麻瓜漫画 | 品牌介绍与联系方式"
        description="非麻瓜漫画品牌介绍——专注魔法漫画、奇幻漫画与异能漫画的在线阅读平台。了解我们的品牌故事、核心价值和联系方式，畅享魔法世界漫画大全。"
        keywords="非麻瓜漫画,关于我们,品牌介绍,漫画平台,在线漫画,联系我们"
        canonicalUrl="https://anhuicloud.cn/about"
      />
      <SchemaOrg data={{
        '@type': 'AboutPage',
        name: '关于非麻瓜漫画',
        description: '非麻瓜漫画品牌介绍——专注魔法漫画、奇幻漫画与异能漫画的在线阅读平台。',
        url: 'https://anhuicloud.cn/about',
      }} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-4">
              <Breadcrumb items={[{ name: '首页', href: '/' }, { name: '关于我们' }]} />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于非麻瓜漫画
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-3xl">
              非麻瓜漫画，一个专注于魔法、奇幻和异能题材的漫画阅读平台。我们相信在平凡的世界之外，还有一个属于魔法与奇迹的天地等待你去探索。
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                  <p>
                    非麻瓜漫画诞生于一个简单的想法：为所有热爱魔法和奇幻世界的读者打造一个专属的漫画家园。在哈利·波特的世界里，「麻瓜」指的是不会魔法的普通人，而我们希望每一位来到非麻瓜漫画的读者，都能找到属于自己的魔法时刻。
                  </p>
                  <p>
                    从最初的几十部漫画起步，我们始终坚持「优质内容 + 极佳体验」的核心理念。通过下拉式阅读模式、智能推荐系统和简洁美观的界面设计，让每一位读者都能沉浸在魔法世界的精彩中。
                  </p>
                  <p>
                    如今，非麻瓜漫画已经发展成为国内领先的魔法奇幻漫画平台，拥有数千部优质漫画作品，涵盖魔法、奇幻、异能、玄幻、冒险、都市异能等多个热门品类。我们与数百位优秀的漫画创作者建立了深度合作关系，持续为读者带来高质量的原创和正版漫画内容。
                  </p>
                </div>
              </div>
              <div className="lg:w-[420px] shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20illustration%20of%20a%20cozy%20reading%20nook%20with%20floating%20magical%20books%20and%20soft%20golden%20glow%2C%20fantasy%20elements%20blending%20with%20modern%20interior%2C%20ethereal%20atmosphere%2C%20detailed%20anime-inspired%20art%2C%20peaceful%20and%20inspiring&width=840&height=600&seq=about-story&orientation=landscape"
                  alt="非麻瓜漫画品牌故事"
                  title="非麻瓜漫画品牌故事"
                  className="w-full rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                品牌历程
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                从零到一，从梦想到现实。回顾非麻瓜漫画的成长之路。
              </p>
            </div>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-300"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div key={idx} className="relative pl-12 md:pl-16">
                    <div className={`absolute left-0 md:left-2 w-5 h-5 rounded-full border-4 border-background-50 ${
                      idx === milestones.length - 1 ? 'bg-primary-500' : 'bg-secondary-500'
                    }`}></div>
                    <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium mb-2">
                      {m.year}
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-1">{m.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{m.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                核心价值
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                这些理念驱动着我们不断前行，为读者和创作者创造更好的平台。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, idx) => (
                <div key={idx} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-xl">
                    <i className={`${v.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                无论你是读者、创作者还是合作伙伴，我们都期待听到你的声音。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-xl">
                  <i className="ri-mail-line text-xl text-accent-500"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">商务合作</h3>
                <p className="text-xs text-foreground-500">business@anhuicloud.cn</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-send-plane-line text-xl text-primary-500"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">作者投稿</h3>
                <p className="text-xs text-foreground-500">submit@anhuicloud.cn</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-xl">
                  <i className="ri-customer-service-2-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">客服支持</h3>
                <p className="text-xs text-foreground-500">support@anhuicloud.cn</p>
              </div>
            </div>

            <ContactForm />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9ooaoaftmtnfcfh0av0';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="true" className="bg-background-50 rounded-xl p-6 md:p-8">
      <h3 className="font-heading font-semibold text-foreground-950 mb-6">发送消息</h3>

      <div className="anti-spam-field">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="你的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="your@email.com"
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="消息主题"
        />
      </div>

      <div className="mb-5">
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
          placeholder="请描述您的问题或建议（500字以内）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>

      {formStatus === 'success' && (
        <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <i className="ri-check-line mr-2"></i>消息已发送成功，我们会尽快回复你！
        </div>
      )}
      {formStatus === 'error' && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <i className="ri-error-warning-line mr-2"></i>{formError}
        </div>
      )}

      <button
        type="submit"
        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        发送消息
      </button>
    </form>
  );
}